import { useState, useMemo } from 'react';
import { Globe, Search, User, ExternalLink, Loader2, ChevronDown, ChevronRight, MessageSquare } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import { Badge } from '@/components/ui/badge';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import { MessageItem } from './MessageItem';
import type { PublicMessage, PublicConversation, TelegramMessage } from '../types';

interface PublicMessagesPanelProps {
  messages: PublicMessage[];
  isLoading: boolean;
  onOpenChat?: (chatId: number) => void;
  onOpenOrder?: (orderId: number) => void;
}

/**
 * Группирует сообщения по отправителю
 * Сообщения от одного отправителя, которые идут подряд, группируются вместе
 */
interface MessageGroup {
  fromId: number;
  isOutgoing: boolean;
  messages: PublicMessage[];
}

function groupMessagesBySender(messages: PublicMessage[]): MessageGroup[] {
  const groups: MessageGroup[] = [];

  for (const message of messages) {
    const lastGroup = groups.at(-1);
    const isOutgoing = message.isOutgoing ?? false;
    const fromId = message.fromId;

    // Для исходящих сообщений - каждое в своей группе
    // Для входящих - группируем от одного отправителя
    const shouldNewGroup =
      !lastGroup ||
      isOutgoing ||
      lastGroup.isOutgoing ||
      lastGroup.fromId !== fromId;

    if (shouldNewGroup) {
      groups.push({
        fromId,
        isOutgoing,
        messages: [message],
      });
    } else {
      // Добавляем в существующую группу входящих сообщений
      lastGroup.messages.push(message);
    }
  }

  return groups;
}

/**
 * Группирует сообщения по chatId в диалоги
 */
function groupMessagesIntoConversations(messages: PublicMessage[]): PublicConversation[] {
  const conversationMap = new Map<number, PublicConversation>();

  for (const msg of messages) {
    if (!conversationMap.has(msg.chatId)) {
      conversationMap.set(msg.chatId, {
        chatId: msg.chatId,
        chatTitle: msg.chatTitle || msg.fromName || 'Неизвестный чат',
        messages: [],
        lastMarkedAt: msg.markedPublicAt || msg.telegramDate,
        messageCount: 0,
      });
    }

    const conversation = conversationMap.get(msg.chatId)!;
    conversation.messages.push(msg);
    conversation.messageCount++;

    // Обновляем lastMarkedAt если это сообщение новее
    const msgDate = msg.markedPublicAt || msg.telegramDate;
    if (msgDate > conversation.lastMarkedAt) {
      conversation.lastMarkedAt = msgDate;
    }
  }

  // Сортируем сообщения внутри каждого диалога по telegramDate
  for (const conversation of conversationMap.values()) {
    conversation.messages.sort((a, b) =>
      new Date(a.telegramDate).getTime() - new Date(b.telegramDate).getTime()
    );
  }

  // Сортируем диалоги по lastMarkedAt (последние сверху)
  return [...conversationMap.values()].sort((a, b) =>
    new Date(b.lastMarkedAt).getTime() - new Date(a.lastMarkedAt).getTime()
  );
}

export function PublicMessagesPanel({
  messages,
  isLoading,
  onOpenChat,
  onOpenOrder,
}: PublicMessagesPanelProps) {
  const [searchQuery, setSearchQuery] = useState('');
  const [expandedConversations, setExpandedConversations] = useState<Set<number>>(new Set());

  // Фильтрация сообщений
  const filteredMessages = useMemo(() => {
    if (!searchQuery) {return messages;}
    const query = searchQuery.toLowerCase();
    return messages.filter(msg =>
      msg.text?.toLowerCase().includes(query) ||
      msg.fromName?.toLowerCase().includes(query) ||
      msg.markedByAdminName?.toLowerCase().includes(query) ||
      msg.chatTitle?.toLowerCase().includes(query)
    );
  }, [messages, searchQuery]);

  // Группировка в диалоги
  const conversations = useMemo(() =>
    groupMessagesIntoConversations(filteredMessages),
    [filteredMessages]
  );

  const toggleConversation = (chatId: number) => {
    setExpandedConversations(prev => {
      const next = new Set(prev);
      if (next.has(chatId)) {
        next.delete(chatId);
      } else {
        next.add(chatId);
      }
      return next;
    });
  };

  return (
    <div className="h-full border-l flex flex-col bg-background overflow-hidden">
      {/* Header */}
      <div className="p-4 border-b">
        <div className="flex items-center gap-2 mb-3">
          <Globe className="h-5 w-5 text-primary" />
          <h2 className="font-semibold">Публичные сообщения</h2>
          {conversations.length > 0 && (
            <Badge variant="secondary" className="ml-auto">
              {conversations.length} {getDeclension(conversations.length, ['диалог', 'диалога', 'диалогов'])}
            </Badge>
          )}
        </div>
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Поиск..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-9"
          />
        </div>
      </div>

      {/* Conversations list */}
      <div className="flex-1 min-h-0 overflow-auto telegram-scrollbar-hidden">
        {isLoading ? (
          <div className="flex items-center justify-center h-32">
            <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
          </div>
        ) : (conversations.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-32 text-muted-foreground">
            <Globe className="h-8 w-8 mb-2 opacity-50" />
            <p className="text-sm">
              {searchQuery ? 'Ничего не найдено' : 'Нет публичных сообщений'}
            </p>
          </div>
        ) : (
          <div className="p-2 space-y-2">
            {conversations.map((conversation) => (
              <ConversationItem
                key={conversation.chatId}
                conversation={conversation}
                isExpanded={expandedConversations.has(conversation.chatId)}
                onToggle={() => toggleConversation(conversation.chatId)}
                onOpenChat={onOpenChat}
                onOpenOrder={onOpenOrder}
              />
            ))}
          </div>
        ))}
      </div>
    </div>
  );
}

/**
 * Адаптирует PublicMessage для использования с MessageItem
 * Преобразует в формат TelegramMessage
 */
interface MessageItemWrapperProps {
  message: PublicMessage;
  chatId: number;
  showAvatar?: boolean;
  showSenderName?: boolean;
  onOpenOrder?: (orderId: number) => void;
}

function MessageItemWrapper({
  message,
  chatId,
  showAvatar = true,
  showSenderName = true,
  onOpenOrder,
}: MessageItemWrapperProps) {
  // Преобразуем PublicMessage в формат, совместимый с MessageItem
  const telegramMessage: TelegramMessage = {
    id: message.id,
    text: message.text || '',
    date: new Date(message.telegramDate),
    editDate: undefined,
    fromId: message.fromId,
    fromName: message.fromName,
    fromUsername: message.fromUsername,
    isOutgoing: message.isOutgoing ?? false,
    isPublic: true,
    views: 0,
    reactions: [],
    replyToMessage: undefined,
    forwardInfo: undefined,
    media: message.media || undefined,
    webPage: undefined,
    entities: message.entities || [],
  };

  return (
    <div className="pb-1">
      <MessageItem
        message={telegramMessage}
        chatId={chatId}
        isSelected={false}
        isRead
        onClick={() => {}}
        onContextMenu={(e) => e.preventDefault()}
        showAvatar={showAvatar}
        showSenderName={showSenderName}
      />
      {/* Show linked order badge if exists */}
      {message.linkedOrderId && (
        <div className="pl-10 mt-1">
          <Badge
            variant="outline"
            className="text-xs cursor-pointer hover:bg-accent"
            onClick={() => onOpenOrder?.(message.linkedOrderId!)}
          >
            Заказ #{message.linkedOrderId}
          </Badge>
        </div>
      )}
    </div>
  );
}

interface ConversationItemProps {
  conversation: PublicConversation;
  isExpanded: boolean;
  onToggle: () => void;
  onOpenChat?: (chatId: number) => void;
  onOpenOrder?: (orderId: number) => void;
}

function ConversationItem({
  conversation,
  isExpanded,
  onToggle,
  onOpenChat,
  onOpenOrder,
}: ConversationItemProps) {
  // Получаем последнее сообщение для превью
  const lastMessage = conversation.messages.at(-1);
  // Уникальные админы, которые помечали сообщения
  const admins = [...new Set(conversation.messages.map(m => m.markedByAdminName).filter(Boolean))];

  return (
    <Collapsible open={isExpanded} onOpenChange={onToggle}>
      <div className="rounded-lg border bg-card overflow-hidden">
        {/* Conversation header */}
        <CollapsibleTrigger asChild>
          <div className="p-3 cursor-pointer hover:bg-accent/50 transition-colors">
            <div className="flex items-center gap-2">
              {/* Expand/collapse icon */}
              {isExpanded ? (
                <ChevronDown className="h-4 w-4 text-muted-foreground flex-shrink-0" />
              ) : (
                <ChevronRight className="h-4 w-4 text-muted-foreground flex-shrink-0" />
              )}

              {/* Avatar */}
              <Avatar className="h-8 w-8 flex-shrink-0">
                <AvatarFallback>
                  <MessageSquare className="h-4 w-4" />
                </AvatarFallback>
              </Avatar>

              {/* Title and preview */}
              <div className="flex-1 min-w-0">
                <div className="flex items-center justify-between gap-2">
                  <span className="font-medium text-sm truncate">
                    {conversation.chatTitle}
                  </span>
                  <span className="text-xs text-muted-foreground flex-shrink-0">
                    {formatTime(conversation.lastMarkedAt)}
                  </span>
                </div>
                {!isExpanded && (
                  <p className="text-xs text-muted-foreground truncate">
                    {lastMessage?.text || '[Медиа]'}
                  </p>
                )}
              </div>

              {/* Badge with message count */}
              <Badge variant="secondary" className="flex-shrink-0">
                {conversation.messageCount}
              </Badge>
            </div>

            {/* Admins info */}
            {admins.length > 0 && !isExpanded && (
              <div className="mt-1 ml-10 flex items-center gap-1 text-xs text-muted-foreground">
                <User className="h-3 w-3" />
                <span className="truncate">
                  {admins.slice(0, 2).join(', ')}
                  {admins.length > 2 && ` +${admins.length - 2}`}
                </span>
              </div>
            )}
          </div>
        </CollapsibleTrigger>

        {/* Expanded messages */}
        <CollapsibleContent>
          <div className="border-t">
            {/* Open chat button */}
            {onOpenChat && (
              <div className="px-3 py-2 border-b bg-muted/30">
                <Button
                  variant="ghost"
                  size="sm"
                  className="w-full justify-start text-xs"
                  onClick={() => onOpenChat(conversation.chatId)}
                >
                  <ExternalLink className="h-3 w-3 mr-2" />
                  Открыть чат
                </Button>
              </div>
            )}

            {/* Messages grouped by sender */}
            <div className="p-3 space-y-1">
              {groupMessagesBySender(conversation.messages).map((group, groupIndex) => (
                <div key={`${group.fromId}-${groupIndex}`}>
                  {group.messages.map((message, index) => (
                    <MessageItemWrapper
                      key={message.id}
                      message={message}
                      chatId={conversation.chatId}
                      showAvatar={index === 0 && !group.isOutgoing}
                      showSenderName={index === 0 && !group.isOutgoing}
                      onOpenOrder={onOpenOrder}
                    />
                  ))}
                  {/* Add spacing between different senders' message groups */}
                  {!group.isOutgoing && groupIndex < groupMessagesBySender(conversation.messages).length - 1 && (
                    <div className="h-2" />
                  )}
                </div>
              ))}
            </div>
          </div>
        </CollapsibleContent>
      </div>
    </Collapsible>
  );
}

function formatTime(dateString: string): string {
  const date = new Date(dateString);
  const now = new Date();
  const diffDays = Math.floor((now.getTime() - date.getTime()) / (1000 * 60 * 60 * 24));

  if (diffDays === 0) {
    return date.toLocaleTimeString('ru', { hour: '2-digit', minute: '2-digit' });
  }

  if (diffDays === 1) {
    return 'Вчера';
  }

  if (diffDays < 7) {
    return date.toLocaleDateString('ru', { weekday: 'short' });
  }

  return date.toLocaleDateString('ru', { day: 'numeric', month: 'short' });
}

function getDeclension(n: number, forms: [string, string, string]): string {
  const n100 = n % 100;
  const n10 = n % 10;

  if (n100 > 10 && n100 < 20) {return forms[2];}
  if (n10 === 1) {return forms[0];}
  if (n10 > 1 && n10 < 5) {return forms[1];}
  return forms[2];
}
